import { useDispatch, useSelector } from 'react-redux';
import { type RootState } from 'useTypedSelector';
import { fetchProductionTags } from 'actions/Dictionary';
import { useEffect } from 'react';

import SelectAll, { ALL_SELECTED_VALUE } from '../SelectAll';
import { type Mode } from './types/Mode';
import { type Tag } from './types/Tag';
import MenuList from './MenuList/MenuList';

const getSortedTags = (tags: Tag[]) =>
  tags.toSorted((a, b) => a.label.localeCompare(b.label));

type ProductionTagSelectTypes = {
  '@id': string | null;
  '@type': string;
  id: number;
  value: string | null;
  position: number | null;
  systemValue: string | null;
  deletedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  icon: string | null;
  iconVisible: number | null;
};

type TagSelectProps = {
  selectedTags: Tag['value'][];
  selectedMode: Mode;
  onChange: (newMode: Mode, newSelectedTags: Tag['value'][]) => void;
  customComponents?: { [key: string]: unknown };
  menuWidth?: number;
};

const ProductionTagSelect = ({
  selectedTags,
  selectedMode,
  onChange,
  customComponents,
  menuWidth,
}: TagSelectProps) => {
  const dispatch = useDispatch();

  const productionTags = useSelector(
    (state: RootState) => state.Dictionary.productionTags
  );

  useEffect(() => {
    if (productionTags.length === 0) {
      dispatch(fetchProductionTags());
    }
  }, []);

  const getTagOptions = (mealTagsOptions: ProductionTagSelectTypes[] = []) =>
    mealTagsOptions.reduce<Tag[]>((acc, tag) => {
      const tagIri = tag?.['@id'];
      const tagLabel = tag?.value;

      if (!tagIri || !tagLabel || acc.some(({ value }) => value === tagIri)) {
        return acc;
      }

      acc.push({
        value: tagIri,
        label: tagLabel,
      });

      return acc;
    }, []);

  const tagOptions = getTagOptions(productionTags || []);

  const selectedTagOptions = getSortedTags(
    selectedTags.reduce<Tag[]>((acc, selectedTag) => {
      const matchedTag = tagOptions.find(tag => tag.value === selectedTag);

      if (matchedTag) {
        acc.push(matchedTag);
      }

      return acc;
    }, [])
  );

  return (
    <SelectAll
      customProps={{
        selectedTags,
        selectedMode,
        onChange,
      }}
      mapBy="label"
      trackBy="value"
      options={getSortedTags(tagOptions)}
      optionSelected={[
        tagOptions.length > 0 &&
          tagOptions.length === selectedTags.length && {
            value: ALL_SELECTED_VALUE,
          },
        ...selectedTagOptions,
      ].filter(Boolean)}
      handleChange={(
        newOptions: {
          value: string;
          label: string;
          isSeparator: boolean;
        }[]
      ) => {
        onChange(
          selectedMode,
          newOptions
            .filter(({ value }) => value !== ALL_SELECTED_VALUE)
            .map(({ value }) => value)
        );
      }}
      components={{
        MenuList,
        ...(customComponents || {}),
      }}
      menuWidth={menuWidth}
    />
  );
};

export default ProductionTagSelect;
